import { useCallback, useEffect, useMemo, useState } from 'react';
import cemeteryBackground from '../../../../assets/fondocementerio.webp';
import nacanacaLogo from '../../../../assets/logo.webp';
import {
  playReviewChime,
  preloadReviewAudio,
  setReviewMuted,
  startReviewMusic,
  stopReviewMusic,
} from '../../infrastructure/reviewAudio';
import { buildReviewSlides } from './ReviewSlides';
import './ReviewScreen.css';

const DEFAULT_SLIDE_MS = 7000;
const PARTICLES = ['✦', '👻', '✧', '💀', '✦', '🕯️', '✧', '✦', '🎬', '✧', '✦', '👻'];

function Particles() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {PARTICLES.map((glyph, index) => (
        <span
          key={`${glyph}-${index}`}
          className="rv-particle text-[#F7E04B]"
          style={{
            left: `${(index * 8.3 + 4) % 100}%`,
            fontSize: `${12 + (index % 4) * 6}px`,
            animationDuration: `${9 + (index % 5) * 2.4}s`,
            animationDelay: `${index * 0.9}s`,
            '--rv-sway': `${index % 2 ? 36 : -30}px`,
            '--rv-spin': `${index % 2 ? 60 : -45}deg`,
          }}
        >
          {glyph}
        </span>
      ))}
    </div>
  );
}

/**
 * Review final del festival, estilo historias: una portada que pide el toque
 * (desbloquea el audio), diapositivas con avance automático y una pantalla
 * final estática con redes. Toca a la derecha para avanzar y a la izquierda
 * para volver.
 */
export default function ReviewScreen({ review, username, loading, error, onClose }) {
  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);

  const replay = useCallback(() => setIndex(0), []);
  const close = useCallback(() => {
    stopReviewMusic();
    onClose();
  }, [onClose]);
  const slides = useMemo(
    () => buildReviewSlides({ review, username: username || 'jugador', onReplay: replay, onClose: close }),
    [review, username, replay, close],
  );
  const slide = slides[Math.min(index, slides.length - 1)];
  const isLast = index >= slides.length - 1;
  const durationMs = slide.durationMs || DEFAULT_SLIDE_MS;

  useEffect(() => {
    preloadReviewAudio();
    return () => stopReviewMusic();
  }, []);

  // Avance automático; la pantalla final es estática.
  useEffect(() => {
    if (!started || paused || slide.isStatic) return undefined;
    const timeoutId = window.setTimeout(
      () => setIndex((current) => Math.min(current + 1, slides.length - 1)),
      durationMs,
    );
    return () => window.clearTimeout(timeoutId);
  }, [started, paused, index, slide.isStatic, durationMs, slides.length]);

  useEffect(() => {
    if (started && slide.chime) playReviewChime();
  }, [started, index, slide.chime]);

  useEffect(() => {
    const handleVisibility = () => setPaused(document.hidden);
    const handleKey = (event) => {
      if (event.key === 'Escape') close();
      if (!started) return;
      if (event.key === 'ArrowRight') setIndex((current) => Math.min(current + 1, slides.length - 1));
      if (event.key === 'ArrowLeft') setIndex((current) => Math.max(current - 1, 0));
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('keydown', handleKey);
    };
  }, [started, slides.length, close]);

  const begin = () => {
    startReviewMusic();
    setIndex(0);
    setStarted(true);
  };
  const toggleMuted = () => {
    const next = !muted;
    setMuted(next);
    setReviewMuted(next);
  };
  // Toque en la mitad derecha avanza; en el tercio izquierdo retrocede.
  const handleTap = (event) => {
    if (!started || event.target.closest('a, button')) return;
    const goBack = event.clientX < window.innerWidth * 0.3;
    setIndex((current) => (goBack ? Math.max(current - 1, 0) : Math.min(current + 1, slides.length - 1)));
  };

  const colors = started ? slide.colors : ['#7C3AED', '#0A1A0F'];
  const controlClass = [
    'flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/45',
    'text-lg text-white active:bg-black/70',
  ].join(' ');

  return (
    <div
      className="rv-root fixed inset-0 z-[80] overflow-hidden text-center text-white"
      style={{ '--rv-a': colors[0], '--rv-b': colors[1] }}
      role="dialog"
      aria-label="Tu review del festival Ñaca Ñaca"
      onPointerUp={handleTap}
    >
      <div className="rv-bg" aria-hidden="true" />
      <div className="rv-photo" style={{ backgroundImage: `url(${cemeteryBackground})` }} aria-hidden="true" />
      <div className="rv-vignette" aria-hidden="true" />
      <Particles />

      <header className="absolute inset-x-0 top-0 z-20 px-3 pt-[max(.75rem,env(safe-area-inset-top))]">
        {started && (
          <div className="flex gap-1" aria-hidden="true">
            {slides.map((item, position) => (
              <span
                key={item.id}
                className={[
                  'rv-progress h-1 flex-1 overflow-hidden rounded-full bg-white/25',
                  position < index || (position === index && item.isStatic) ? 'rv-done' : '',
                  position === index && !item.isStatic ? 'rv-active' : '',
                ].join(' ')}
              >
                <span
                  key={`${item.id}-${index}`}
                  style={{
                    animationDuration: `${item.durationMs || DEFAULT_SLIDE_MS}ms`,
                    animationPlayState: paused ? 'paused' : 'running',
                  }}
                />
              </span>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center justify-between">
          <button
            type="button"
            onClick={toggleMuted}
            aria-pressed={muted}
            aria-label={muted ? 'Activar música' : 'Silenciar música'}
            className={controlClass}
          >
            <span aria-hidden="true">{muted ? '🔇' : '🔊'}</span>
          </button>
          <button type="button" onClick={close} aria-label="Cerrar review" className={controlClass}>
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      </header>

      {!started ? (
        <main className="relative z-10 mx-auto flex h-full max-w-md flex-col items-center justify-center px-6">
          <img src={nacanacaLogo} alt="Ñaca Ñaca" className="rv-pop rv-float-img w-44" />
          <p
            className="rv-in mt-6 text-[11px] font-black uppercase tracking-[.3em] text-[#F7E04B]"
            style={{ '--d': '.4s' }}
          >
            El festival llegó a su fin
          </p>
          <h1
            className="rv-in rv-glow mt-3 font-display text-5xl uppercase leading-none text-white"
            style={{ '--d': '.6s' }}
          >
            Tu Review
          </h1>
          <p className="rv-in mt-4 max-w-xs text-base leading-7 text-[#E9DDF7]" style={{ '--d': '.9s' }}>
            Un recuento de todo lo que viviste en La Maldición Ñaca Ñaca. Sube el volumen.
          </p>
          {error ? (
            <p className="rv-in mt-6 text-sm text-[#ffd8de]" role="alert">{error}</p>
          ) : (
            <button
              type="button"
              onClick={begin}
              disabled={loading}
              className={[
                'rv-in rv-cta mt-8 min-h-[56px] w-full max-w-xs rounded-sm border border-white px-6 py-4',
                'font-black uppercase tracking-[.14em] text-black disabled:opacity-60',
              ].join(' ')}
              style={{ '--d': '1.2s' }}
            >
              {loading ? 'Preparando tu recuento…' : '▶ Ver mi review'}
            </button>
          )}
          <button
            type="button"
            onClick={close}
            className="rv-in mt-4 min-h-[44px] px-4 text-xs font-bold uppercase tracking-widest text-white/75 underline"
            style={{ '--d': '1.4s' }}
          >
            Ahora no, ir al mapa
          </button>
        </main>
      ) : (
        <main
          key={`${slide.id}-${index}`}
          className={[
            'absolute inset-0 z-10',
            slide.align === 'scroll' ? 'rv-scroll overflow-y-auto' : 'overflow-hidden',
          ].join(' ')}
        >
          {/* El scroll ocupa toda la pantalla (barra en el borde de la ventana); la columna va centrada dentro. */}
          <div
            className={[
              'rv-column mx-auto flex max-w-md flex-col px-6',
              'pt-[max(5rem,calc(env(safe-area-inset-top)+4.75rem))]',
              isLast
                ? 'pb-[max(1.25rem,env(safe-area-inset-bottom))]'
                : 'pb-[max(2.75rem,calc(env(safe-area-inset-bottom)+2.25rem))]',
              slide.align === 'scroll' ? 'min-h-full' : 'h-full',
              slide.align === 'top' ? 'justify-start' : 'justify-center',
              slide.wide ? 'rv-wide' : '',
            ].join(' ')}
          >
            <div>{slide.content}</div>
          </div>
          {!isLast && (
            <p className="pointer-events-none absolute inset-x-0 bottom-[max(1rem,env(safe-area-inset-bottom))] text-[10px] font-bold uppercase tracking-[.2em] text-white/45">
              Toca para continuar
            </p>
          )}
        </main>
      )}
    </div>
  );
}
